import { FolderIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import compact from 'lodash/compact';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useStudentDashboard } from '@/data/context/StudentDashboardContext';
import useCourseLessonsHref from '@/data/hook/course-progress/useCourseLessonsHref';
import { BreadcrumbItem } from '@/data/hook/useDynamicBreadcrumbItems';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import { ROUTES } from '@/utils/routes';

export default function useCourseLessonsBreadcrumbs(): BreadcrumbItem[] {
  const { slugCourseName = '' } = useParams();
  const { activeCourseProgress, isLoading } = useStudentDashboard();
  const { t } = useTranslation('translation', { keyPrefix: 'breadcrumb' });
  const courseLessonsHref = useCourseLessonsHref();

  const { data: coursePath } = useQuery({
    ...coursePathsQueryKeys.get(slugCourseName),
    enabled: !isLoading && !activeCourseProgress && !!slugCourseName,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const courseName =
    activeCourseProgress?.coursePath.course.name ?? coursePath?.course.name;

  return useMemo(() => {
    const icon = <FolderIcon className="size-4" />;

    return compact([
      {
        id: 'myCourses',
        href: ROUTES.DASHBOARD.BASE,
        children: t('home'),
        icon,
      },
      courseName && {
        id: 'course',
        href: courseLessonsHref,
        children: courseName,
        icon,
      },
    ]);
  }, [t, courseName, courseLessonsHref]);
}
